import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { poolReviewApi, TYPE_LABEL_KEY, type QuestionForReview } from '@/api/ieltsMock';
import { Card, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Pill } from '@/components/ui/Pill';

/**
 * PoolKeyReview — вопросы, у которых ключ разошёлся с выводом модели.
 *
 * Пометка ставится при генерации разбора: модель сначала выводит ответ из
 * текста сама, потом сравнивает с записанным ключом. Расхождение НЕ меняет
 * ключ автоматически — модель ошибается не реже генератора, и подмена
 * верного ключа на неверный хуже, чем сомнительный ключ.
 *
 * Цена ошибки в живой системе: ученик отвечает правильно, получает ошибку,
 * тип уходит в «слабые» — и тренажёр начинает лечить то, чего нет.
 */
export function PoolKeyReview() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['pool', 'review'], queryFn: poolReviewApi.list });

  const resolve = useMutation({
    mutationFn: ({ id, key }: { id: string; key?: string }) => poolReviewApi.resolve(id, key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pool', 'review'] }),
  });

  if (q.isLoading) return <Card variant="subtle">{t('poolKeys.loading')}</Card>;

  const items = q.data?.items ?? [];
  if (items.length === 0) {
    return (
      <Card variant="subtle" className="text-center">
        <p className="text-on-surface-variant">{t('poolKeys.empty')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-on-surface-variant">
        {t('poolKeys.intro')} {t('poolKeys.totalLabel')} <b>{items.length}</b>
      </p>
      {items.map((it) => (
        <ReviewCard
          key={it.id}
          item={it}
          busy={resolve.isPending}
          onKeep={() => resolve.mutate({ id: it.id })}
          onReplace={(key) => resolve.mutate({ id: it.id, key })}
        />
      ))}
    </div>
  );
}

function ReviewCard({
  item,
  busy,
  onKeep,
  onReplace,
}: {
  item: QuestionForReview;
  busy: boolean;
  onKeep: () => void;
  onReplace: (key: string) => void;
}) {
  const { t } = useTranslation();
  const [newKey, setNewKey] = useState('');
  const [showSource, setShowSource] = useState(false);

  let options: string[] = [];
  try {
    options = JSON.parse(item.options || '[]');
  } catch {
    options = [];
  }

  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Pill variant="warning">{t('poolKeys.toReview')}</Pill>
        <span className="text-xs text-on-surface-variant">
          {item.skill} · {t(TYPE_LABEL_KEY[item.type] ?? item.type)}
        </span>
      </div>

      <CardTitle>{item.prompt}</CardTitle>

      <p className="text-sm">
        {t('poolKeys.currentKey')} <b>{item.answer_key}</b>
      </p>

      {options.length > 0 && (
        <p className="text-sm text-on-surface-variant">{t('poolKeys.variants', { list: options.join(' · ') })}</p>
      )}

      <p className="rounded-lg bg-warning-container/40 p-3 text-sm">{item.note}</p>

      {/* Источник рядом: решать, не открывая другую страницу. Без текста
          перед глазами проверить ключ невозможно. */}
      {item.source && (
        <div>
          <button
            onClick={() => setShowSource((v) => !v)}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t(showSource ? 'poolKeys.hideSource' : 'poolKeys.showSource')} {t('poolKeys.sourceWord')}{item.title ? ` — ${item.title}` : ''}
          </button>
          {showSource && (
            <p className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-surface-container-low p-3 text-sm leading-relaxed">
              {item.source}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-outline-variant pt-3">
        <Button variant="ghost" disabled={busy} onClick={onKeep}>
          {t('poolKeys.keyOk')}
        </Button>
        <input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder={t('poolKeys.newKey')}
          className="rounded-lg border border-outline bg-surface px-3 py-2 text-sm"
        />
        <Button disabled={busy || !newKey.trim()} onClick={() => onReplace(newKey.trim())}>
          {t('poolKeys.replaceKey')}
        </Button>
      </div>
    </Card>
  );
}
